import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Utils } from '../../services/utils.service';
import { Product } from '../../../planogram/models/product';
import { SessionService } from '../../services/session.service';
import { TranslateService } from '@ngx-translate/core';

import { SHARED_MODULES } from '../../../shared.';
import { GetLinkPipe } from '../../pipes/get-link.pipe';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { PhIconWrapperComponent } from '../ph-icon-wrapper/ph-icon-wrapper.component';

@Component({
    selector: 'app-data-table',
    templateUrl: './data-table.component.html',
    styleUrls: ['./data-table.component.scss'],
	imports: [SHARED_MODULES, GetLinkPipe, FaIconComponent, PhIconWrapperComponent],
    standalone: true
})
export class DataTableComponent implements OnInit {
	paginateShow: boolean = false;
	tableData: {
		columns: Array<any>,
		data: Array<any>
	};
	paginateInfo: {
		from: number;
		to: number;
		of: number;
		max_page: number;
	};
	page: number = 1;
	paginationButtons: Array<any>;

	data: Array<any>;
	columns: Array<any>;
	displayData: Array<any>;
	maxHeight: any = 'none';
	groups_to_expand: Set<string> = new Set<string>();
	dataGrouped: boolean = false;
	sort_params: any;
	firtstLoad: boolean = true;
	show_all: boolean = false;
	products: Product[] = [];
	deletedProduct: boolean = false;
	keyGroup: any;
	previousKey: any;

	@Input() roundData: boolean = true;
	@Input() showTools: boolean = false;
	@Input() changePaginateCount: boolean = false;
	@Input() generateLink: { func: any, column: string } = { func: false, column: '' };
	@Input() fixedHeader: boolean = false;
	@Input() pointInComplectation: boolean = false;
	@Input() hiddenLongText: boolean = false;
	@Input() isComplectation: boolean = false;
	@Input() defaultSort: boolean = false;
	@Input() enablePagination: boolean = true;
	@Input() analyticTable: boolean = false;
	@Input('paginateCount') paginateCount: number = 25;

	@Input('dataGrouped') set setDataGrouped(value: boolean) {
		this.dataGrouped = value;
		this.firtstLoad = true;
		this.groups_to_expand = new Set<string>();
		if (this.tableData) {
			this.processAndDisplayData();
		}
	}

	@Input('keyGroup') set setGroupedKey(value: any) {
		this.previousKey = this.keyGroup;
		this.keyGroup = value;
	}

	@Input('tableData') set setTableData(value: { columns: Array<any>, data: Array<any> }) {
		if (this.tableData === value && this.data === value.data) {
			return;
		}

		this.tableData = value;
		this.columns = this.tableData.columns.map(i => {
			const upperKey: string = i.toUpperCase();
			if (this.translate.instant(upperKey) !== upperKey) i = upperKey;
			return { name: i, sort: false };
		});
		this.data = value.data;

		this.groups_to_expand = new Set<string>();
		this.firtstLoad = true;

		this.processAndDisplayData();
	}

	@Input('products') set setProducts(value: any) {
		this.products = value;
	}

	@Input('maxHeight') set setMaxHeight(value: any) {
		this.maxHeight = value;
		if (this.dataTable) {
			this.dataTable.nativeElement.style.setProperty('max-height', `${this.maxHeight}px`);
		}
	}

	@Input('showAll') set setShowAll(value: boolean) {
		this.show_all = value;
		if (this.tableData) {
			this.setDisplayedData();
		}
	}

	@Output('set_paginate') set_paginate: EventEmitter<any> = new EventEmitter<any>();

	@ViewChild('dataTable', { static: true }) dataTable: ElementRef;

	constructor(private translate: TranslateService, public session: SessionService) {
	}

	ngOnInit(): void {
		if (!this.paginateCount) {
			this.paginateCount = this.changePaginateCount ? 10 : 25;
		}

		if (this.tableData && this.tableData.data && this.tableData.columns) {
			this.processAndDisplayData();
		}
	}

	private processAndDisplayData(): void {
		this.createLinks();

		if (this.defaultSort) {
			const initialSortColumn = this.columns[0];
			if (initialSortColumn) {
				this.sort(initialSortColumn, true, true);
			} else {
				this.setDisplayedData();
			}
		} else {
			this.setDisplayedData();
		}

		if (this.isComplectation) {
			this.columns = this.columns.filter(row => row.name !== 'STATUS');
			this.data = this.data.map(row => row.filter(el => ![true, false].includes(el)));
			this.setDisplayedData();
		}

		if (this.analyticTable && !this.dataGrouped) {
			this.show_all = true;
			this.setDisplayedData();
		}

		if (this.maxHeight && this.dataTable) {
			this.dataTable.nativeElement.style.setProperty('max-height', `${this.maxHeight}px`);
		}
	}

	setDisplayedData(): void {
		let processedData = [...this.data];

		if (this.dataGrouped) {
			processedData = ((processedData
				.map((d, index) => {
					if (!d.parent) {
						d.parent = {};
					}

					const parentKey = Array.isArray(d.parent) ? d.parent.join('-') : (d.parent.name || '');
					const uniqueId = `group-${parentKey}-${index}`;

					d.parent.uniqueId = uniqueId;
					d.parent.parent = true;

					d.children.forEach(c => {
						c.hidden = !this.groups_to_expand.has(uniqueId);
					});
					return [d.parent, ...d.children];
				}) as any)
				.flat())
				.filter(row => !row.hidden);
		}

		let l: number = 0, r: number = processedData.length;
		let paginateCount: number = this.paginateCount;

		if (this.showTools && this.enablePagination && paginateCount > 0 && !this.show_all) {
			l = (this.page - 1) * paginateCount;
			r = l + paginateCount;
		}

		this.displayData = processedData.slice(l, r);

		this.paginateInfo = {
			from: l ? l + 1 : 1,
			to: (processedData.length > r) ? r : processedData.length,
			of: processedData.length,
			max_page: this.show_all ? 1 : Math.ceil(processedData.length / paginateCount)
		};
		this.setPaginationButtons();
	}

	simpleSort(arr, idx, key?, sortType?) {
		return arr.sort((a, b): number =>
			key
				? Utils.generalSorting({
					firstEl: a[key][idx] || 0,
					secondEl: b[key][idx] || 0,
					order: sortType
				})
				: a[idx] && b[idx] && a[idx].name && b[idx].name
					? Utils.generalSorting({
						firstEl: a[idx].name || 0,
						secondEl: b[idx].name || 0,
						order: sortType
					})
					: Utils.generalSorting({
						firstEl: a[idx] || 0,
						secondEl: b[idx] || 0,
						order: sortType
					})
		);
	}

	compareValues(array, key, index, sortType?) {
		return array.sort((a, b): number => {
			const getSafeValue = (item, propKey, idx) => {
				if (item && item[propKey] && (Array.isArray(item[propKey]) || typeof item[propKey] === 'string')) {
					return item[propKey][idx] || 0;
				}
				return 0;
			};

			const firstEl = getSafeValue(a, key, index);
			const secondEl = getSafeValue(b, key, index);

			return Utils.generalSorting({
				firstEl: firstEl,
				secondEl: secondEl,
				order: sortType
			});
		});
	}

	sortData(index: number, sortType: boolean) {
		return this.simpleSort(this.data, index, null, sortType);
	}

	sortGroupedData(index, sortType) {
		let arr;

		arr = this.compareValues(this.data, 'parent', index, sortType);
		arr.forEach(item => {
			if (item.children && Array.isArray(item.children)) {
				item.children = this.simpleSort(item.children, index, null, sortType);
			} else {
				item.children = [];
			}
		});

		return arr;
	}

	sort(th: any, change_order: boolean = true, defaultSort: boolean = false): void {
		let index: number = this.columns.findIndex(c => c.name === th.name);
		if (index === -1) return;

		let sortType: boolean = !th.sort;

		if (change_order) {
			this.columns.forEach(item => {
				item.class = '';
				item.sort = false;
			});

			th.sort = sortType;
			th.class = sortType ? 'sorting_desc' : 'sorting_asc';
			this.sort_params = th.name;
		} else {
			th.class = sortType ? 'sorting_desc' : 'sorting_asc';
		}

		if (defaultSort) {
			th.class = 'sorting_asc';
			sortType = false;
		}

		if (this.dataGrouped) {
			this.data = this.sortGroupedData(index, sortType);
		} else {
			this.data = this.sortData(index, sortType);
		}

		this.setDisplayedData();
	}

	paginateBack(): void {
		if (this.page > 1) {
			this.page -= 1;
			this.setDisplayedData();
		}
	}

	paginateForward(): void {
		if (this.page < this.paginateInfo.max_page) {
			this.page += 1;
			this.setDisplayedData();
		}
	}

	paginate(value: number): void {
		if (value < 1 || value > this.paginateInfo.max_page) return;
		this.page = value;
		this.setDisplayedData();
	}

	setPaginationButtons(): void {
		let btns = [],
			max_page: number = this.paginateInfo.max_page;

		if (max_page <= 5) {
			for (let i: number = 1; i <= max_page; i++) {
				btns.push(i);
			}
			this.paginationButtons = btns;
			return;
		}

		if (this.page < 5) {
			for (let i: number = 1; i <= 5; i++) {
				btns.push(i);
			}
			btns.push('...');
			btns.push(max_page);
			this.paginationButtons = btns;
			return;
		}

		if (max_page - this.page < 4) {
			for (let i: number = max_page; i >= max_page - 4; i--) {
				btns.push(i);
			}
			btns.push('...');
			btns.push(1);
			btns.reverse();
			this.paginationButtons = btns;
			return;
		}

		this.paginationButtons = [1, '...', this.page - 1, this.page, this.page + 1, '...', max_page];
	}

	setPaginateCount(value: number): void {
		this.show_all = !~value;
		this.paginateCount = (value != -1) ? value : this.data.length;
		this.setDisplayedData();

		if (this.paginateInfo.from >= this.data.length) {
			this.paginate(1);
		}

		this.set_paginate.emit(this.paginateCount);
	}

	createLinks(): void {
		if (this.generateLink.func) {
			let index: number = this.columns.findIndex(i => i.name === this.generateLink.column);
			if (index != -1) {
				this.data.forEach(i => {
					if (i[index]) {
						i[index] = this.generateLink.func(i[index]);
					}
				});
			}
		}
	}

	getType(value) {
		switch (true) {
			case (typeof value == 'string'):
				return 'string';

			case (typeof value == 'object' && value && value.link):
				return 'link';

			default:
				return 'default';
		}
	}

	formatNumber(value) {
		if (typeof value != 'number') return value;

		let number,
			decimal,
			temp = '';

		if (value == Utils.round(value, 0)) {
			value = String(value);
			number = value.split('').reverse().join('');
		} else {
			value = String(value);
			number = value.split('.')[0].split('').reverse().join('');
			decimal = value.split('.')[1];
		}

		for (let index: number = 0; index < number.length; index++) {
			temp += number[index];
			if (((index + 1) % 3 == 0) && (index != number.length - 1) && (number[index + 1] != '-')) {
				temp += ' ';
			}
		}

		temp = temp.split('').reverse().join('');

		if (decimal) temp += '.' + decimal;

		return temp;
	}

	toggleChildrenVisibility(groupParent: any): void {
		const uniqueId = groupParent.uniqueId;
		if (this.groups_to_expand.has(uniqueId)) {
			this.groups_to_expand.delete(uniqueId);
		} else {
			this.groups_to_expand.add(uniqueId);
		}
		this.setDisplayedData();
	}

	findInactiveProduct(td: any): boolean {
		if (!td) return;
		if (!td.name || !this.products.length) return;

		return !this.products.filter((el: Product): boolean => el.name == td.name)[0].is_active;
	}

}
